/* ============================================================================
   FB Product Pinning — Dawn HYBRID port (2026-07-22)
   Based on Den Liu's "FB Shop Secret Sauce" (v2025-08-02-b) + Orange Loaf's
   proven Horizon Path A. See projects/2026-05-12-fb-product-pinning/DAWN-PORT-PLAN.md

   Behavior: /collections/<any>?first=<handle | encoded product URL>
   - If the product's card is already in the rendered grid -> move it to slot #1
     (0 fetches, instant). Otherwise fetch /products/<handle>?view=card (native
     Dawn card via templates/product.card.liquid) and insert it at slot #1.
   - NO grid wipe: pagination, filtering, sorting, SEO all stay native.
   - `variant=` (inside the ?first= URL, or as &variant=) = COLOUR CARRY
     (2026-10-15, registry "FB pinning colour carry"): the card is fetched as
     ?variant=<id>&view=card, which renders that colour's photo and links to the
     same colour's lowest-price variant (templates/product.card.liquid). Used for
     on-page pins too (it REPLACES the on-page card, so the count is unchanged).
     The fetch is normally already running: snippets/ol-head.liquid starts it in
     <head> (window.__olPinCard). If that line is missing it is started here.
     Price stays Dawn's "From $<min>"; size is never carried. No variant = the
     old behaviour exactly (on-page move, 0 fetches).
   - `source=` / `limit=` are ignored (no cross-collection fill in the hybrid).
============================================================================ */

(() => {
  const qp = new URLSearchParams(location.search);

  /* -- parse ?first= : plain handle, path, or full encoded FB product URL -- */
  const rawFirst = decodeURIComponent((qp.get('first') || '').trim());
  if (!rawFirst) return;

  let firstHandle = '';
  if (rawFirst.startsWith('http')) {
    try {
      const u = new URL(rawFirst);
      const segs = u.pathname.split('/').filter(Boolean);
      if (segs[0] === 'products' && segs[1]) firstHandle = segs[1];
    } catch (_) { /* malformed URL -> no pin */ }
  } else {
    firstHandle = rawFirst.split('/').filter(Boolean).pop() || '';
  }
  firstHandle = firstHandle.split('?')[0].toLowerCase();
  if (!firstHandle) return;
  const variantMatch = rawFirst.match(/[?&]variant=(\d+)/);
  const firstVariant = variantMatch ? variantMatch[1] : (qp.get('variant') || '').replace(/\D/g, '');

  const grid = document.querySelector('ul#product-grid, ul[id^="product-grid"], .product-grid');
  if (!grid) return;

  /* -- theme-editor / preview passthrough (lets ?preview_theme_id= testing work) -- */
  const previewExtras = ['preview_theme_id', 'preview_theme_token', 'pb']
    .filter(k => qp.has(k))
    .map(k => `${k}=${encodeURIComponent(qp.get(k) || '')}`)
    .join('&');
  const withPreview = url =>
    previewExtras ? url + (url.includes('?') ? '&' : '?') + previewExtras : url;

  /* -- 1) already rendered on this page? exact /products/<handle> segment match -- */
  const findOnPage = () => {
    for (const a of grid.querySelectorAll('li.grid__item a[href*="/products/"]')) {
      try {
        const segs = new URL(a.href, location.origin).pathname.split('/').filter(Boolean);
        if (segs[segs.length - 1] === firstHandle) return a.closest('li.grid__item');
      } catch (_) { /* skip bad href */ }
    }
    return null;
  };

  const FULL_PAGE = 16; // products_per_page in templates/collection.json — keep in sync.
                        // 16/page is the WeScale-optimal count (Shan, 2026-07-22): a pinned
                        // landing shows pin + top 15; the page's LAST card sits out on those
                        // views only. Deliberate — do not "fill up" past 16 (tried, rejected).

  /* -- COLOUR CARRY: ad link has a variant -> always use the colour card -- */
  if (firstVariant) {
    const early = window.__olPinCard;
    const cardHtml = early && early.handle === firstHandle && early.variant === firstVariant
      ? early.promise
      : fetch(withPreview(`/products/${encodeURIComponent(firstHandle)}?variant=${firstVariant}&view=card`), {
          credentials: 'same-origin',
          cache: 'no-cache'
        }).then(r => (r.ok ? r.text() : '')).catch(() => '');
    cardHtml.then(html => {
      html = (html || '').trim();
      if (!html.startsWith('<li')) return pinDefault(); // failed -> old behaviour (default colour)
      const items = grid.querySelectorAll('li.grid__item');
      const existing = findOnPage();
      grid.insertAdjacentHTML('afterbegin', html);
      if (existing) existing.remove();                       // on-page: swap, count unchanged
      else if (items.length >= FULL_PAGE) items[items.length - 1].remove(); // off-page: keep 16
    });
    return;
  }
  pinDefault();

  function pinDefault() {
  const onPage = findOnPage();
  if (onPage) {
    if (grid.firstElementChild !== onPage) grid.prepend(onPage);
    onPage.classList.remove('scroll-trigger--offscreen'); // never hide slot #1 behind the scroll animation
    return;
  }

  /* -- 2) not on this page: fetch the native card fragment and insert at #1 --
     cache:'no-cache' is deliberate — force-cache served STALE fragments on the
     old theme (learned 2026-07-07). Do not "optimize" this back. */
  fetch(withPreview(`/products/${encodeURIComponent(firstHandle)}?view=card`), {
    credentials: 'same-origin',
    cache: 'no-cache'
  })
    .then(r => (r.ok ? r.text() : ''))
    .then(html => {
      html = (html || '').trim();
      if (!html.startsWith('<li')) return; // 404/junk -> leave the page untouched
      const beforeCount = grid.querySelectorAll('li.grid__item').length;
      grid.insertAdjacentHTML('afterbegin', html);
      /* keep the page at exactly FULL_PAGE cards: drop the (least-valuable)
         last card. Partial pages (last page / filtered) are left alone. */
      if (beforeCount >= FULL_PAGE) {
        const items = grid.querySelectorAll('li.grid__item');
        items[items.length - 1].remove();
      }
    })
    .catch(() => { /* network fail -> page stays native */ });
  }
})();
